import React, { useEffect } from 'react';
import { Grid, Typography, Box, CircularProgress, useTheme } from '@mui/material';
import { Inventory, ShoppingBag, Visibility, LocalOffer, RemoveShoppingCart, Warehouse } from '@mui/icons-material';
import PortalCard from '../../../../../components/PortalCard';
import analyticsService from '../../../../../services/analytics/AnalyticsService';
import { Moment } from 'moment';

interface ProductSummaryStatsProps {
    dateRange?: [Moment, Moment];
}

const ProductSummaryStats: React.FC<ProductSummaryStatsProps> = ({ dateRange }) => {
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState<{
        totalProducts: number;
        activeProducts: number;
        totalViews: number;
        avgViewsPerProduct: number;
        productsOnSale: number;
        productsWithZeroViews: number;
        totalStock: number;
    } | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const startDate = dateRange?.[0]?.toISOString();
                const endDate = dateRange?.[1]?.toISOString();
                const result = await analyticsService.getProductSummaryStats(startDate, endDate);
                if (result.success && result.data) {
                    setData(result.data);
                } else {
                    setError(result.message || 'Failed to fetch summary stats');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch summary stats');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange]);

    const cards = [
        {
            title: 'Total Products',
            value: data?.totalProducts || 0,
            icon: <Inventory sx={{ fontSize: 18, color: 'white' }} />,
            iconBgColor: '#2196F3'
        },
        {
            title: 'Total Views',
            value: data?.totalViews || 0,
            icon: <Visibility sx={{ fontSize: 18, color: 'white' }} />,
            iconBgColor: '#FF9800'
        },
        {
            title: 'Avg Views/Product',
            value: data?.avgViewsPerProduct?.toFixed(1) || '0.0',
            icon: <ShoppingBag sx={{ fontSize: 18, color: 'white' }} />,
            iconBgColor: '#4CAF50'
        },
        {
            title: 'Products on Sale',
            value: data?.productsOnSale || 0,
            icon: <LocalOffer sx={{ fontSize: 18, color: 'white' }} />,
            iconBgColor: '#9C27B0'
        },
        {
            title: 'Zero Views',
            value: data?.productsWithZeroViews || 0,
            icon: <RemoveShoppingCart sx={{ fontSize: 18, color: 'white' }} />,
            iconBgColor: '#F44336'
        },
        {
            title: 'Total Stock',
            value: data?.totalStock || 0,
            icon: <Warehouse sx={{ fontSize: 18, color: 'white' }} />,
            iconBgColor: '#607D8B'
        }
    ];

    return (
        <Grid container spacing={2}>
            {cards.map((card, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                    <PortalCard
                        sx={{
                            p: 2,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignSelf: 'center',
                                padding: 2,
                                backgroundColor: card.iconBgColor,
                                marginRight: 2,
                                flexShrink: 0,
                            }}
                        >
                            {card.icon}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                            <Typography variant='subtitle1' color='text.secondary' fontFamily='primaryFont'>
                                {card.title}
                            </Typography>
                            {loading ? (
                                <CircularProgress size={24} sx={{ mt: 0.5 }} />
                            ) : error ? (
                                <Typography variant='body2' color='error' fontFamily='primaryFont' sx={{ mt: 0.5 }}>
                                    {error}
                                </Typography>
                            ) : (
                                <Typography variant='h5' fontWeight='bold' fontFamily='primaryFont'>
                                    {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                                </Typography>
                            )}
                        </Box>
                    </PortalCard>
                </Grid>
            ))}
        </Grid>
    );
};

export default ProductSummaryStats;

